import { useState } from 'react'
import { api } from '../lib/api'

function formatPhone(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 10)
  return '+1' + digits
}

function toLocalISOString(dateStr, timeStr) {
  return `${dateStr}T${timeStr}:00`
}

function toLocalInputs(isoString) {
  // Handle both "2026-04-14T09:40:00" and "2026-04-14T13:40:00Z" formats
  const str = isoString.replace('Z', '')
  const d = new Date(str)
  // If string has no timezone, parse directly
  let date, hours, mins
  if (!isoString.includes('Z') && !isoString.includes('+')) {
    // No timezone — treat as local, parse manually
    const parts = str.split('T')
    date = parts[0]
    const timeParts = parts[1].split(':')
    hours = timeParts[0]
    mins = timeParts[1]
  } else {
    date = d.toLocaleDateString('en-CA')
    hours = String(d.getHours()).padStart(2, '0')
    mins = String(d.getMinutes()).padStart(2, '0')
  }
  return { date, time: `${hours}:${mins}` }
}

export default function EditJobModal({ job, onClose, onJobEdited }) {
  const { date: initDate, time: initTime } = toLocalInputs(job.appointment_time)

  const [form, setForm] = useState({
    customer_name: job.customer_name || '',
    customer_phone: (job.customer_phone || '').replace(/^\+1/, ''),
    service_type: job.service_type || '',
    tech_name: job.tech_name || '',
    job_number: job.job_number || '',
    appointment_date: initDate,
    appointment_time: initTime,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  function handleChange(e) {
    let value = e.target.value
    if (e.target.name === 'customer_phone') {
      value = value.replace(/\D/g, '').slice(0, 10)
    }
    setForm(prev => ({ ...prev, [e.target.name]: value }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await api.updateJob(job.id, {
        customer_name: form.customer_name,
        customer_phone: formatPhone(form.customer_phone),
        service_type: form.service_type || null,
        tech_name: form.tech_name || null,
        job_number: form.job_number || null,
        appointment_time: toLocalISOString(form.appointment_date, form.appointment_time),
      })
      onJobEdited()
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <h2 style={s.modalTitle}>Edit Job</h2>
          <button style={s.closeBtn} onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} style={s.form}>
          <label style={s.label}>Customer Name *</label>
          <input style={s.input} name="customer_name" value={form.customer_name} onChange={handleChange} required />

          <label style={s.label}>Customer Phone *</label>
          <input style={s.input} name="customer_phone" placeholder="3215550000" value={form.customer_phone} onChange={handleChange} required maxLength={10} inputMode="numeric" />
          <p style={s.hint}>{form.customer_phone.replace(/\D/g,'').length}/10 digits</p>

          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Date *</label>
              <input style={s.input} name="appointment_date" type="date" value={form.appointment_date} onChange={handleChange} required />
            </div>
            <div style={s.field}>
              <label style={s.label}>Time *</label>
              <input style={s.input} name="appointment_time" type="time" value={form.appointment_time} onChange={handleChange} required />
            </div>
          </div>

          <label style={s.label}>Service Type</label>
          <input style={s.input} name="service_type" value={form.service_type} onChange={handleChange} />

          <label style={s.label}>Technician Name</label>
          <input style={s.input} name="tech_name" value={form.tech_name} onChange={handleChange} />

          <label style={s.label}>Job Number <span style={{color:'#4a4a60', fontWeight:400}}>(internal only)</span></label>
          <input style={s.input} name="job_number" placeholder="JOB-1042" value={form.job_number} onChange={handleChange} />

          {error && <p style={s.error}>{error}</p>}

          <div style={s.actions}>
            <button type="button" style={s.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={s.submitBtn} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    padding: '20px',
  },
  modal: {
    background: '#13131a', border: '1px solid #1e1e2e',
    borderRadius: '16px', padding: '24px 20px 32px',
    width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  modalTitle: { color: '#fff', fontSize: 18, fontWeight: 700, margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#6b6b80', fontSize: 20, cursor: 'pointer' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#a0a0b8', fontSize: 13, fontWeight: 600 },
  hint: { color: '#4a4a60', fontSize: 11, margin: '-6px 0 0' },
  input: {
    background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8,
    padding: '12px 14px', color: '#fff', fontSize: 15, outline: 'none', colorScheme: 'dark',
    width: '100%', boxSizing: 'border-box',
  },
  error: { color: '#ff4d4d', fontSize: 13 },
  actions: { display: 'flex', gap: 10, marginTop: 8 },
  cancelBtn: {
    flex: 1, background: 'none', border: '1px solid #2a2a3a', color: '#6b6b80',
    borderRadius: 8, padding: '12px 0', fontSize: 14, cursor: 'pointer',
  },
  submitBtn: {
    flex: 2, background: '#4f46e5', color: '#fff', border: 'none',
    borderRadius: 8, padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
}
